import { Fab } from '@/components/elements/forms/buttons';
import Button from '@/components/elements/forms/buttons/Button/Button';
import Icon from '@/components/icons/Icon';
import { classnames } from '@/helpers';
import { _t } from '@/locale';
import { MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

type Props = {
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  loading: boolean;
  fetching: boolean;
  onLoadMore: () => void;
  onPageChange: (page: number) => void;
};

/**
 * Builds a pagination window of 4 pages (currentPage-1 to currentPage+2) with
 * ellipses if there are more pages outside the window.
 * Always includes the first and last page if they are outside the window.
 * Near the edges, extends to show 5 pages so the ellipsis isn't immediately adjacent.
 */
function buildPageWindow(currentPage: number, totalPages: number): (number | '...')[] {
  let rangeStart = Math.max(1, currentPage - 1);
  let rangeEnd = Math.min(totalPages, currentPage + 2);

  // Near the start: extend right so at least 5 pages show before the ellipsis
  if (rangeStart === 1) rangeEnd = Math.min(totalPages, Math.max(rangeEnd, 5));
  // Near the end: extend left symmetrically
  if (rangeEnd === totalPages) rangeStart = Math.max(1, Math.min(rangeStart, totalPages - 4));

  const pages: (number | '...')[] = [];

  if (rangeStart > 1) {
    pages.push(1);
    if (rangeStart > 2) pages.push('...');
  }

  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.push(i);
  }

  if (rangeEnd < totalPages) {
    if (rangeEnd < totalPages - 1) pages.push('...');
    pages.push(totalPages);
  }

  return pages;
}

const MPLoadMorePaginator = ({
  currentPage,
  totalPages,
  hasMore,
  loading,
  fetching,
  onLoadMore,
  onPageChange,
}: Props) => {
  const location = useLocation();
  const showPaginator = totalPages > 1;

  if (!hasMore && !showPaginator) return null;

  const buildPageHref = (page: number): string => {
    const sp = new URLSearchParams(location.search);
    if (page === 1) sp.delete('page');
    else sp.set('page', String(page));
    const query = sp.toString();
    return query ? `${location.pathname}?${query}` : location.pathname;
  };

  const handlePageClick = (page: number) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (!fetching) onPageChange(page);
  };

  const pages = showPaginator ? buildPageWindow(currentPage, totalPages) : [];
  const prevDisabled = fetching || currentPage <= 1;
  const nextDisabled = fetching || currentPage >= totalPages;

  return (
    <div className="py-xl mx-auto flex w-full flex-col items-center" style={{ maxWidth: 320 }}>
      {hasMore && (
        <Button
          variant="primary"
          size="sm"
          block
          label={_t('loadMore')}
          onClick={onLoadMore}
          disabled={loading || fetching}
        />
      )}

      {showPaginator && (
        <div
          className={classnames(
            'gap-x-md flex w-full items-center justify-between',
            hasMore ? 'pb-xl py-md' : 'py-xl',
          )}>
          {prevDisabled ? (
            <span className="pointer-events-none flex h-8 w-8 items-center justify-center rounded">
              <Icon variant="chevron-left" color="fg_disabled" style={{ width: 20, height: 20 }} />
            </span>
          ) : (
            <Fab
              disabled={prevDisabled}
              variant="secondary"
              className="flex-shrink-0"
              to={buildPageHref(currentPage - 1)}
              aria-label={_t('previous')}
              size="sm"
              icon={<Icon variant="chevron-left" style={{ width: 20, height: 20 }} />}
            />
          )}

          <div className="gap-xxs flex flex-row items-center justify-between">
            {pages.map((page, i) =>
              page === '...' ? (
                <span key={`ellipsis-${i}`} className="text-fg_disabled text-m flex h-8 items-center justify-center">
                  …
                </span>
              ) : (
                <Link
                  key={page}
                  to={buildPageHref(page)}
                  onClick={handlePageClick(page)}
                  className={classnames(
                    'text-m px-xs flex h-8 items-center justify-center',
                    page === currentPage
                      ? 'text-fg_alwaysLight bg-bg_primary pointer-events-none w-8 rounded-full'
                      : 'text-fg_link',
                    fetching && page !== currentPage && 'pointer-events-none opacity-50',
                  )}
                  aria-current={page === currentPage ? 'page' : undefined}>
                  {page}
                </Link>
              ),
            )}
          </div>

          {nextDisabled ? (
            <span className="pointer-events-none flex h-8 w-8 items-center justify-center rounded">
              <Icon variant="chevron-right" color="fg_disabled" style={{ width: 20, height: 20 }} />
            </span>
          ) : (
            <Fab
              variant="secondary"
              className="flex-shrink-0"
              to={buildPageHref(currentPage + 1)}
              aria-label={_t('next')}
              size="sm"
              icon={<Icon variant="chevron-right" style={{ width: 20, height: 20 }} />}
              onClick={handlePageClick(currentPage + 1)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MPLoadMorePaginator;
